import React, { useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { FilePdfOutlined, LinkOutlined, SwapOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { Success } from "../../../../commons/Messages/Messages";
import { openBlob } from "../api";
import Amount from "../shared/Amount";
import MoneyInput from "../shared/MoneyInput";
import { formatCents, formatDate } from "../shared/money";
import {
  fetchSelfBillingPdf,
  getCreditOptions,
  linkCreditNote,
  objectSelfBilling,
  offsetCredit
} from "./api";

const docLabel = doc =>
  doc ? [doc.invoiceNumber, doc.belegNumber].filter(Boolean).join(" · ") || "—" : "—";

/**
 * The credit side of a Beleg in the review drawer:
 *  - credit note: which invoice it corrects (link action), credit left (offset action)
 *  - supplier invoice: how much credit notes settled
 *  - self-billing Gutschrift: number, PDF, "Widerspruch erfasst"
 */
const CreditPanel = ({ beleg, canEdit, onChanged }) => {
  const { t } = useTranslation("accounting");
  const [dialog, setDialog] = useState(null); // "link" | "offset" | "objection"
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  if (!beleg) return null;
  const isCreditNote = beleg.kind === "credit_note";
  const isSelfBilling = beleg.kind === "self_billing";
  const credited = beleg.creditedAmount || 0;
  if (!isCreditNote && !isSelfBilling && !credited) return null;

  const open = async kind => {
    form.resetFields();
    setDialog(kind);
    if (kind === "objection") {
      form.setFieldsValue({ date: dayjs() });
      return;
    }
    setOptions(null);
    try {
      const res = await getCreditOptions(beleg._id);
      setOptions(res.data);
      if (kind === "offset") form.setFieldsValue({ amount: null });
      if (kind === "link") form.setFieldsValue({ invoice: beleg.corrects?._id || undefined });
    } catch (err) {
      generalErrorAlert(err);
      setDialog(null);
    }
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      let res;
      if (dialog === "link") {
        res = await linkCreditNote(beleg._id, values.invoice || null);
        Success(
          res.data.applied
            ? t("Linked; {{amount}} settled on the invoice", {
                amount: formatCents(res.data.applied)
              })
            : t("Credit note linked")
        );
      } else if (dialog === "offset") {
        res = await offsetCredit(beleg._id, {
          target: values.target,
          amount: values.amount || undefined
        });
        Success(t("{{amount}} offset", { amount: formatCents(res.data.amount) }));
      } else {
        res = await objectSelfBilling(beleg._id, {
          reason: values.reason,
          date: values.date ? values.date.format("YYYY-MM-DD") : undefined
        });
        Success(t("Objection recorded; the booking was reversed"));
      }
      setDialog(null);
      onChanged?.(res.data.beleg);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setBusy(false);
    }
  };

  const settled = (beleg.creditAllocations || []).some(a => a.kind === "credit_note");
  const booked = Boolean(beleg.journalEntry);
  const invoiceOption = row => ({
    value: row._id,
    label: `${docLabel(row)} · ${formatDate(row.invoiceDate)} · ${formatCents(row.gross)}${
      row.booked ? ` (${t("open")} ${formatCents(row.open)})` : ` (${t("not booked yet")})`
    }`
  });

  return (
    <>
      {isCreditNote && (
        <Alert
          type={beleg.corrects ? "info" : "warning"}
          showIcon
          className="mb-3"
          message={
            beleg.corrects
              ? t("Corrects invoice {{ref}}", { ref: docLabel(beleg.corrects) })
              : beleg.referencedInvoiceNumber
                ? t("Refers to invoice {{ref}}, not found", { ref: beleg.referencedInvoiceNumber })
                : t("Not linked to an invoice")
          }
          description={
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {beleg.corrects && (
                <Typography.Text type="secondary">
                  {beleg.correctsSource === "auto"
                    ? t("Linked automatically")
                    : t("Linked by hand")}
                  {settled ? ` · ${t("settled")}` : ""}
                </Typography.Text>
              )}
              {booked && (
                <span>
                  {t("Credit left with the supplier")}:{" "}
                  <Amount cents={beleg.remainingCredit || 0} strong />
                </span>
              )}
              {(beleg.creditAllocations || [])
                .filter(a => a.amount > 0)
                .map((a, i) => (
                  <div key={i}>
                    <Tag>{a.kind === "offset" ? t("Offset") : t("Settlement")}</Tag>
                    {docLabel(a.beleg)} · {formatDate(a.date)} · <Amount cents={a.amount} />
                  </div>
                ))}
              {canEdit && (
                <Space wrap>
                  {!settled && (
                    <Button size="small" icon={<LinkOutlined />} onClick={() => open("link")}>
                      {beleg.corrects ? t("Change link") : t("Link to invoice")}
                    </Button>
                  )}
                  {booked && (beleg.remainingCredit || 0) > 0 && (
                    <Button size="small" icon={<SwapOutlined />} onClick={() => open("offset")}>
                      {t("Offset against invoice")}
                    </Button>
                  )}
                </Space>
              )}
              {booked && (beleg.remainingCredit || 0) > 0 && (
                <Typography.Text type="secondary">
                  {t(
                    "A refund by the supplier is recorded by matching the incoming bank line to this credit note."
                  )}
                </Typography.Text>
              )}
            </Space>
          }
        />
      )}

      {credited > 0 && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("Credited by supplier credit notes: {{amount}}", {
            amount: formatCents(credited)
          })}
          description={
            <Space direction="vertical" size={2}>
              <span>
                {t("Open")}:{" "}
                <Amount
                  cents={Math.max(
                    0,
                    (beleg.amounts?.gross || 0) - (beleg.paidAmount || 0) - credited
                  )}
                  strong
                />
              </span>
              {(beleg.credits || []).map((c, i) => (
                <div key={i}>
                  <Tag>{c.kind === "offset" ? t("Offset") : t("Credit note")}</Tag>
                  {docLabel(c.creditNote)} · {formatDate(c.date)} · <Amount cents={c.amount} />
                </div>
              ))}
            </Space>
          }
        />
      )}

      {isSelfBilling && (
        <Alert
          type={beleg.status === "objected" ? "error" : "info"}
          showIcon
          className="mb-3"
          message={t("Gutschrift (self-billing, § 14 Abs. 2 S. 2 UStG)")}
          description={
            <>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label={t("Number")}>
                  {beleg.selfBilling?.number || t("Draft")}
                </Descriptions.Item>
                {beleg.selfBilling?.reverseCharge && (
                  <Descriptions.Item label={t("Tax treatment")}>
                    {t("Reverse charge (§ 13b UStG)")}
                  </Descriptions.Item>
                )}
                {beleg.status === "objected" && (
                  <Descriptions.Item label={t("Objection")}>
                    {formatDate(beleg.selfBilling?.objectionDate)} ·{" "}
                    {beleg.selfBilling?.objectionReason}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Space wrap>
                <Button
                  size="small"
                  icon={<FilePdfOutlined />}
                  onClick={() => openBlob(fetchSelfBillingPdf(beleg._id)).catch(generalErrorAlert)}
                >
                  {beleg.status === "draft" ? t("Preview PDF") : t("PDF")}
                </Button>
                {canEdit && ["booked", "paid"].includes(beleg.status) && (
                  <Button size="small" danger onClick={() => open("objection")}>
                    {t("Record objection")}
                  </Button>
                )}
              </Space>
            </>
          }
        />
      )}

      <Modal
        open={Boolean(dialog)}
        onCancel={() => setDialog(null)}
        onOk={submit}
        confirmLoading={busy}
        rootClassName="acct-module"
        destroyOnClose
        okText={
          dialog === "link" ? t("Link") : dialog === "offset" ? t("Offset") : t("Record objection")
        }
        cancelText={t("Cancel")}
        title={
          dialog === "link"
            ? t("Link credit note to invoice")
            : dialog === "offset"
              ? t("Offset credit against invoice")
              : t("Record objection (Widerspruch)")
        }
      >
        <Form form={form} layout="vertical">
          {dialog === "link" && (
            <Form.Item name="invoice" label={t("Supplier invoice")}>
              <Select
                allowClear
                loading={!options}
                placeholder={t("No link")}
                options={(options?.invoices || []).map(invoiceOption)}
              />
            </Form.Item>
          )}
          {dialog === "offset" && (
            <>
              <Typography.Paragraph>
                {t("Credit left")}: <Amount cents={options?.remainingCredit || 0} strong />
              </Typography.Paragraph>
              <Form.Item
                name="target"
                label={t("Supplier invoice")}
                rules={[{ required: true, message: t("Required") }]}
              >
                <Select
                  loading={!options}
                  options={(options?.offsetTargets || []).map(invoiceOption)}
                />
              </Form.Item>
              <Form.Item name="amount" label={t("Amount")} extra={t("Empty: as much as possible")}>
                <MoneyInput />
              </Form.Item>
            </>
          )}
          {dialog === "objection" && (
            <>
              <Alert
                type="warning"
                showIcon
                className="mb-3"
                message={t(
                  "The subcontractor objected: the Gutschrift loses its effect as an invoice. The booking and the input tax are reversed."
                )}
              />
              <Form.Item
                name="date"
                label={t("Date of the objection")}
                rules={[{ required: true, message: t("Required") }]}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="reason"
                label={t("Reason")}
                rules={[{ required: true, message: t("Required") }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CreditPanel;
